import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Star, Trophy, Check, Gamepad2, ChevronRight, Gift, UserPlus,
  Sparkles, Shield, Users, Zap, Crown, Play, ArrowRight, Smartphone, LogIn
} from "lucide-react";
import { getBetsoftGameImage } from "../data/betsoftGameImages";
import LanguageToggle from "../components/LanguageToggle";
import { PaymentTrustStrip, TrustSignalsGrid } from "../components/PaymentTrustStrip";
import { GoldDustParticles, PopularNowWidget } from "../components/HomeAura";
import Mascot from "../components/Mascot";
import LiveWinsTicker from "../components/LiveWinsTicker";
import HeroIslands from "../components/HeroIslands";
import HeroVaultBackdrop from "../components/HeroVaultBackdrop";
import MegaLobby from "../components/MegaLobby";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dc2ea8e9-88c1-4191-a09e-3c70ac39f874/artifacts/lxlb74jo_IMG_9876.jpeg";
const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/jackroyals_/",
  facebook: "https://www.facebook.com/share/g/1AqUU8BF6w/?mibextid=wwXIfr"
};

// Launch: June 1, 2026 at 12:00 AM Eastern (EDT = UTC-4 in June, summer DST).
// 00:00 EDT = 04:00 UTC. DO NOT change without verifying DST offset for the launch date.
const LAUNCH_DATE = new Date("2026-06-01T04:00:00Z");

const LaunchCountdown = () => {
  const calc = () => {
    const diff = LAUNCH_DATE - new Date();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, live: true };
    return {
      d: Math.floor(diff / (1000 * 60 * 60 * 24)),
      h: Math.floor((diff / (1000 * 60 * 60)) % 24),
      m: Math.floor((diff / (1000 * 60)) % 60),
      s: Math.floor((diff / 1000) % 60),
      live: false,
    };
  };
  const [t, setT] = useState(calc());
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  if (t.live) {
    return (
      <div className="mb-8 inline-block px-6 py-3 rounded-2xl bg-emerald-500/20 border border-emerald-500/40">
        <p className="text-emerald-400 font-black text-lg sm:text-xl tracking-wider">🚀 WE'RE LIVE — PLAY NOW</p>
      </div>
    );
  }
  const Box = ({ value, label }) => (
    <div className="text-center">
      <div className="px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl bg-gradient-to-b from-amber-900/60 to-black/80 border border-amber-500/40 backdrop-blur-sm min-w-[64px] sm:min-w-[80px]" data-testid={`countdown-${label.toLowerCase()}`}>
        <p className="text-2xl sm:text-4xl font-black text-amber-300 tabular-nums tracking-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          {String(value).padStart(2, "0")}
        </p>
      </div>
      <p className="text-[9px] sm:text-[10px] text-amber-200/60 mt-1.5 tracking-[0.2em] font-bold uppercase">{label}</p>
    </div>
  );
  return (
    <motion.div
      className="mb-8"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
    >
      <div className="flex items-center justify-center gap-2 sm:gap-4">
        <Box value={t.d} label="Days" />
        <span className="text-2xl sm:text-3xl text-amber-500/40 font-black">:</span>
        <Box value={t.h} label="Hours" />
        <span className="text-2xl sm:text-3xl text-amber-500/40 font-black">:</span>
        <Box value={t.m} label="Mins" />
        <span className="text-2xl sm:text-3xl text-amber-500/40 font-black">:</span>
        <Box value={t.s} label="Secs" />
      </div>
      <p className="text-center text-[10px] sm:text-xs text-amber-200/50 mt-2 tracking-widest font-bold uppercase">
        Until June 1 · 12:00 AM Eastern Time
      </p>
    </motion.div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, login: authLogin } = useAuth();
  const [featuredData, setFeaturedData] = useState(null);
  const [activeGiveaway, setActiveGiveaway] = useState(null);
  const [recentWins, setRecentWins] = useState([]);
  const [publicStats, setPublicStats] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  // Structured login error so we can render an actionable CTA (signup / forgot pwd)
  // instead of a useless "Invalid credentials" toast.
  const [loginError, setLoginError] = useState(null); // {code, message, action}
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetForm, setResetForm] = useState({ password: "", confirm: "" });
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  useEffect(() => {
    if (user) navigate("/lobby");
  }, [user]);

  // Check URL for reset_token or forgot param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    const forgot = params.get("forgot");
    if (token) {
      setResetToken(token);
      setResetMode(true);
      setShowLogin(true);
    } else if (forgot) {
      setForgotMode(true);
      setShowLogin(true);
    }
  }, []);

  // Live active-player counter — was waitlist count during pre-launch, now
  // points at /api/public-stats since the site is LIVE.
  const [livePlayersCount, setLivePlayersCount] = useState(4287);
  const [isLive, setIsLive] = useState(false);
  const [launchedAt, setLaunchedAt] = useState(null);
  const [activePromos, setActivePromos] = useState([]);
  // Disabled providers list — used to hide provider logos/promos on the landing
  // page when an admin has turned off a provider (e.g. Mascot maintenance).
  const [disabledProviders, setDisabledProviders] = useState([]);
  const isProviderHidden = (name) => {
    const n = (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    return disabledProviders.some(dp => {
      const d = (dp || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      return d && (n.includes(d) || d.includes(n));
    });
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/featured-games`).catch(() => ({ data: null })),
      axios.get(`${API}/giveaways/public`).catch(() => ({ data: [] })),
      axios.get(`${API}/recent-wins`).catch(() => ({ data: [] })),
      axios.get(`${API}/public-stats`).catch(() => ({ data: null })),
      axios.get(`${API}/public-stats`).catch(() => ({ data: { players_count: 4287 } })),
      axios.get(`${API}/launch/status`).catch(() => ({ data: { is_live: false } })),
      axios.get(`${API}/promos/active`).catch(() => ({ data: { active: [] } })),
      axios.get(`${API}/disabled-games-list`).catch(() => ({ data: { disabled_providers: [] } })),
    ]).then(([feat, give, wins, stats, wl, launch, promos, disabled]) => {
      setFeaturedData(feat.data);
      const giveaways = Array.isArray(give.data) ? give.data : [];
      if (giveaways.length > 0) setActiveGiveaway(giveaways[0]);
      setRecentWins(Array.isArray(wins.data) ? wins.data : []);
      setPublicStats(stats.data);
      setLivePlayersCount(wl.data?.players_count || wl.data?.count || 4287);
      setIsLive(Boolean(launch.data?.is_live));
      setLaunchedAt(launch.data?.launched_at || null);
      setActivePromos(promos.data?.active || []);
      setDisabledProviders(disabled.data?.disabled_providers || []);
    });
  }, []);

  // Refresh active promos every 5 min so a banner disappears the moment
  // the promo's end_at ticks past. No redeploy required to retire a promo.
  useEffect(() => {
    const poll = setInterval(() => {
      axios.get(`${API}/promos/active`).then(r => {
        setActivePromos(r.data?.active || []);
      }).catch(() => {});
    }, 300000);
    return () => clearInterval(poll);
  }, []);

  const signupPromo = activePromos.find(p => p.id === "signup_bonus_july_2026");
  const firstPurchasePromo = activePromos.find(p => p.id === "first_purchase_50pct_july_2026");

  // Poll launch status — only while NOT live yet; once live, stop polling
  useEffect(() => {
    if (isLive) return;
    const poll = setInterval(() => {
      axios.get(`${API}/launch/status`).then(r => {
        if (r.data?.is_live) {
          setIsLive(true);
          setLaunchedAt(r.data.launched_at);
        }
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(poll);
  }, [isLive]);

  // Poll live player count every 60 seconds
  useEffect(() => {
    const poll = setInterval(() => {
      axios.get(`${API}/public-stats`).then(r => {
        if (r.data?.players_count) setLivePlayersCount(r.data.players_count);
      }).catch(() => {});
    }, 60000);
    return () => clearInterval(poll);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    try {
      await authLogin(loginForm.email, loginForm.password);
      setShowLogin(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend now returns a structured object {code, message, action, identifier}
      // for login failures. Older responses still come as plain strings (banned,
      // chargeback-frozen, etc.) — fall back to a normal toast for those.
      if (detail && typeof detail === "object" && detail.code) {
        setLoginError(detail);
      } else {
        const msg = (typeof detail === "string" ? detail : null)
          || err?.message
          || "Login failed";
        setLoginError({ code: "generic", message: msg, action: null });
      }
    }
    setLoginLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send reset email");
    }
    setForgotLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetForm.password !== resetForm.confirm) {
      alert("Passwords do not match");
      return;
    }
    if (resetForm.password.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: resetForm.password });
      setResetDone(true);
      window.history.replaceState({}, "", "/");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reset password");
    }
    setResetLoading(false);
  };

  const closeLoginModal = (open) => {
    if (!open) {
      setForgotMode(false);
      setForgotSent(false);
      setForgotEmail("");
      setResetMode(false);
      setResetDone(false);
      setResetForm({ password: "", confirm: "" });
    }
    setShowLogin(open);
  };

  // Mixed-provider Top Rated grid. Backend now returns up to 20 games
  // from Mascot/Booming/Betsoft/Redwood in top_arcade_games.games.
  const allGames = [];
  if (featuredData) {
    const top = featuredData.top_arcade_games?.games || [];
    const featured = featuredData.featured_games?.games || [];
    const seen = new Set();
    [...top, ...featured].forEach((g) => {
      const id = g.id || g.gameId || g.name || g.game_name;
      if (id && seen.has(id)) return;
      seen.add(id);
      const provider = g.provider || "";
      // Skip games from disabled providers so Mascot (and any future
      // provider we globally kill) never leaks onto the marketing surface
      // even if the backend still returns them in top_arcade_games.
      if (isProviderHidden(provider) || isProviderHidden(g.provider_name || "")) return;
      // Resolve thumbnail per provider — Betsoft frequently returns an
      // empty/unreachable thumbnail from the backend (CDN at bfrnd.net is
      // DNS-blocked from our pod), so route through the local mapping in
      // betsoftGameImages.js which knows the real /api/game-assets/betsoft/
      // thumb paths.
      let img = g.thumbnail || g.image || "";
      if (!img && provider.toLowerCase().includes("betsoft")) {
        img = getBetsoftGameImage(g.id || g.gameId || "", g.name || g.game_name || "");
      }
      if (!img) {
        img = getBetsoftGameImage(g.id || g.gameId || "", g.name || g.game_name || "");
      }
      if (img && allGames.length < 18) {
        allGames.push({
          name: g.name || g.game_name || "Game",
          image: img,
          provider,
        });
      }
    });
  }
  // Provider chip palette — used for the neon badge on each game card.
  const providerChip = (name) => {
    const p = (name || "").toLowerCase();
    if (p.includes("mascot"))      return { text: "MASCOT",       cls: "bg-cyan-500/15  text-cyan-300  border-cyan-400/40   shadow-[0_0_12px_rgba(34,211,238,0.35)]" };
    if (p.includes("booming"))     return { text: "BOOMING",      cls: "bg-rose-500/15  text-rose-300  border-rose-500/40   shadow-[0_0_12px_rgba(244,63,94,0.35)]"  };
    if (p.includes("betsoft"))     return { text: "BETSOFT",      cls: "bg-amber-500/15 text-amber-300 border-amber-400/40  shadow-[0_0_12px_rgba(251,191,36,0.35)]" };
    if (p.includes("peter"))       return { text: "REDWOOD", cls: "bg-violet-500/15 text-violet-200 border-violet-500/40 shadow-[0_0_12px_rgba(167,139,250,0.35)]" };
    if (p.includes("ka"))          return { text: "KA GAMING",    cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40 shadow-[0_0_12px_rgba(52,211,153,0.35)]" };
    return { text: (name || "ARCADE").toUpperCase(), cls: "bg-slate-700/30 text-slate-300 border-slate-500/40" };
  };

  return (
    <div className="min-h-screen jr-casino-bg relative" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="jr-casino-shimmer" aria-hidden="true" />
      {/* Floating "Gold Dust" luxury aura — GPU-composited, respects prefers-reduced-motion */}
      <GoldDustParticles count={30} />
      {/* ═══ HEADER ═══ */}
      <header className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-amber-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer min-w-0" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="JackRoyals" className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex-shrink-0" />
            <span className="text-base sm:text-xl font-bold text-white truncate" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Jack<span className="text-amber-400">Royals</span>
            </span>
          </div>
          {/* Iconic nav — themed mascots as category chips (Aug 2026) */}
          <div className="hidden md:flex items-center gap-2 text-sm text-slate-300" data-testid="landing-iconic-nav">
            <a href="#popular-now" className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-rose-400/50 transition-all" data-testid="nav-fish-tables">
              <Mascot slug="egyptian_princess" size="sm" role="inline" tip="Cleopatra's fish tables are LIVE 🐠" className="!w-8 !h-8" />
              <span className="text-xs font-black tracking-wider uppercase group-hover:text-rose-200" style={{ fontFamily: "'Unbounded', sans-serif" }}>Fish Tables</span>
            </a>
            <a href="#popular-now" className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-amber-400/50 transition-all" data-testid="nav-hot-games">
              <Mascot slug="viking_warrior" size="sm" role="inline" tip="Reels on fire today! ⚔️" className="!w-8 !h-8" />
              <span className="text-xs font-black tracking-wider uppercase group-hover:text-amber-200" style={{ fontFamily: "'Unbounded', sans-serif" }}>Hot Games</span>
            </a>
            <a href="#games" className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-cyan-400/50 transition-all" data-testid="nav-slots">
              <Mascot slug="wizard" size="sm" role="inline" tip="Cast a spin and win big! 🪄" className="!w-8 !h-8" />
              <span className="text-xs font-black tracking-wider uppercase group-hover:text-cyan-200" style={{ fontFamily: "'Unbounded', sans-serif" }}>Slots</span>
            </a>
            <a href="/signup" className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-purple-400/50 transition-all" data-testid="nav-whale-lounge">
              <Mascot slug="mafia_boss" size="sm" role="inline" tip="Whales only. VIP treatment ♠️" className="!w-8 !h-8" />
              <span className="text-xs font-black tracking-wider uppercase group-hover:text-purple-200" style={{ fontFamily: "'Unbounded', sans-serif" }}>Whale Lounge</span>
            </a>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Language toggle hidden until full ES translation lands */}
            {false && <LanguageToggle compact />}
            <button onClick={() => setShowLogin(true)} className="hidden sm:inline-block text-sm text-slate-300 hover:text-white transition-colors font-medium" data-testid="header-login-btn">
              Sign In
            </button>
            {/* Mobile-only compact Sign In icon */}
            <button onClick={() => setShowLogin(true)} aria-label="Sign In" className="sm:hidden p-2 text-slate-300 hover:text-white transition-colors" data-testid="header-login-btn-mobile">
              <LogIn className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate("/signup")}
              className="bg-amber-400 hover:bg-amber-500 text-slate-950 font-bold rounded-full px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm transition-all hover:-translate-y-0.5 shadow-[0_0_15px_rgba(251,191,36,0.3)] whitespace-nowrap"
              data-testid="header-signup-btn"
            >
              Join Now
            </button>
          </div>
        </div>
      </header>

      {/* ═══ LIVE WINS TICKER — clickable, refreshes daily at 00:00 UTC ═══ */}
      <LiveWinsTicker onItemClick={(item) => navigate(user ? `/lobby?launch=${encodeURIComponent(item.game_id)}` : "/signup")} />

      {/* ═══ 10-DAY FIRST-PURCHASE 50% BONUS BANNER — auto-hides after end_at ═══ */}
      {firstPurchasePromo && (() => {
        const endDate = new Date(firstPurchasePromo.end_at);
        const daysLeft = Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)));
        return (
          <div
            className="relative w-full mt-[100px] mb-2 z-30"
            data-testid="promo-first-purchase-banner"
          >
            <div className="max-w-7xl mx-auto px-3 sm:px-6">
              <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/60 bg-gradient-to-r from-red-950 via-amber-950 to-red-950 shadow-[0_0_50px_-10px_rgba(251,191,36,0.5)]">
                {/* Animated shine sweep */}
                <div className="absolute inset-0 pointer-events-none opacity-30" style={{
                  background: "linear-gradient(120deg, transparent 30%, rgba(255,215,0,0.35) 50%, transparent 70%)",
                  animation: "jr-shine-sweep 3s linear infinite",
                }} />
                <div className="relative flex flex-col sm:flex-row items-center gap-3 sm:gap-5 p-4 sm:p-5">
                  <div className="flex-shrink-0 relative">
                    <div className="absolute inset-0 blur-2xl bg-amber-400/40 rounded-full" />
                    <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-300 via-amber-500 to-orange-500 flex items-center justify-center text-2xl sm:text-3xl font-black text-slate-950 shadow-[0_0_25px_rgba(251,191,36,0.6)] rotate-[-6deg]">
                      +50%
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 text-center sm:text-left">
                    <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
                      <span className="px-2 py-0.5 rounded bg-red-500 text-white text-[10px] font-black uppercase tracking-widest animate-pulse">
                        Limited · {daysLeft}d Left
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold">First 10 Days Only</span>
                    </div>
                    <h3 className="text-lg sm:text-2xl font-black text-white leading-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                      🎁 First Purchase? Get <span className="text-amber-300">+50% BONUS</span> GC <em className="not-italic text-amber-300">AND</em> SC
                    </h3>
                    <p className="text-xs sm:text-sm text-amber-100/90 mt-0.5">
                      Any Gold Coin package <strong className="text-white">$25 or higher</strong> — bonus auto-applied on your first deposit. Ends July 21.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/signup")}
                    className="flex-shrink-0 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-black rounded-full px-5 sm:px-7 py-2.5 sm:py-3 text-sm sm:text-base whitespace-nowrap shadow-[0_0_25px_rgba(251,191,36,0.5)] transition-all hover:-translate-y-0.5"
                    data-testid="promo-first-purchase-cta"
                  >
                    CLAIM 50% BONUS →
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ HERO SECTION — GRAND OPENING ═══ */}
      <section className={`relative ${firstPurchasePromo ? "pt-8 md:pt-12" : "pt-36 md:pt-44"} pb-16 md:pb-24 overflow-hidden`}>
        {/* Golden vault backdrop + card-suit particles — sits behind all hero content */}
        <HeroVaultBackdrop />
        {/* Hero-only accents that layer over the site-wide casino background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#08040d] to-transparent" />
          {/* Animated confetti particles */}
          {[...Array(20)].map((_, i) => (
            <div key={i} className="absolute rounded-sm animate-bounce" style={{
              width: `${4 + Math.random() * 6}px`, height: `${4 + Math.random() * 6}px`,
              background: ['#ffd700', '#ff4444', '#4d7cff', '#ff6b35', '#44ff44'][i % 5],
              left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
              opacity: 0.4 + Math.random() * 0.4,
              animationDelay: `${Math.random() * 3}s`, animationDuration: `${2 + Math.random() * 3}s`,
            }} />
          ))}
          {/* Gold sparkle streaks */}
          <div className="absolute top-20 left-10 w-px h-40 bg-gradient-to-b from-transparent via-amber-400/40 to-transparent rotate-12 animate-pulse" />
          <div className="absolute top-10 right-20 w-px h-52 bg-gradient-to-b from-transparent via-amber-400/30 to-transparent -rotate-12 animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-20 left-1/4 w-px h-32 bg-gradient-to-b from-transparent via-amber-500/35 to-transparent rotate-6 animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            {/* Top badge */}
            {isLive ? (
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-400/15 border border-emerald-400/40 mb-6" data-testid="hero-live-badge">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-bold tracking-[0.15em] uppercase text-emerald-300">🎰 We're Live — Play Now</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-400/15 border border-emerald-400/40 mb-6">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-bold tracking-[0.15em] uppercase text-emerald-300">Now Live · Sign Up &amp; Play</span>
              </div>
            )}

            {/* Main headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-tight mb-4 jr-flicker" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              <span className="bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent jr-gradient-anim">
                WELCOME TO JACKROYALS
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-6 leading-relaxed">
              1,300+ games. Real prizes. Real payouts. Sign up free and get{" "}
              {signupPromo ? (
                <>
                  <span className="text-amber-400 font-bold">{(signupPromo.grant_gc || 300000).toLocaleString()} Gold Coins</span>
                  {" + "}
                  <span className="text-emerald-300 font-bold">{signupPromo.grant_sc || 2} FREE Sweeps Coins</span>
                  {" "}on the house to start playing instantly.
                </>
              ) : (
                <>
                  <span className="text-amber-400 font-bold">300,000 Gold Coins</span>
                  {" + "}
                  <span className="text-emerald-300 font-bold">2 FREE Sweeps Coins</span>
                  {" "}on the house to start playing instantly.
                </>
              )}
            </p>

            {/* Hero Scene — King in the center with 3 floating "islands" (Fish, Slots, Whale) around him */}
            <motion.div
              className="mb-6 md:mb-8 relative"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, type: "spring", stiffness: 110, damping: 16 }}
              data-testid="hero-mascot-artifact"
            >
              <HeroIslands
                navigate={navigate}
                renderKing={() => (
                  <div className="jr-mascot-breathe">
                    <Mascot
                      slug="king"
                      size="xl"
                      role="hero"
                      tip="Tap to claim your gift! 👑"
                      ariaLabel={user ? "Open Daily Wheel" : "Open Sign Up"}
                      onClick={() => {
                        if (user) navigate("/daily-wheel");
                        else navigate("/signup");
                      }}
                    />
                  </div>
                )}
              />
            </motion.div>
            <div className="flex justify-center -mt-2 mb-4 sm:hidden">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[10px] font-black tracking-[0.18em] uppercase animate-pulse"
                data-testid="hero-mascot-tap-hint"
              >
                <span>👆</span> Tap the King · Claim Your Gift
              </span>
            </div>

            {/* Pulsing gold CTA — primary Hero call-to-action (Aug 2026) */}
            <div className="flex justify-center mb-8">
              <button
                onClick={() => navigate(user ? "/daily-wheel" : "/signup")}
                data-testid="hero-claim-gift-btn"
                className="jr-cta-pulse group relative inline-flex items-center gap-2 px-8 sm:px-12 py-4 sm:py-5 rounded-full font-black text-slate-950 text-base sm:text-lg tracking-wider uppercase transition-transform hover:scale-[1.04] active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #FFD700 0%, #FFA500 55%, #FFD700 100%)",
                  boxShadow: "0 0 45px rgba(255,215,0,0.55), inset 0 -3px 0 rgba(0,0,0,0.15)",
                  fontFamily: "'Unbounded', sans-serif",
                }}
              >
                <span aria-hidden className="text-xl">🎁</span>
                Claim Your Gift &amp; Play
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Prize showcase removed (giveaway pics) — replaced with provider firepower stat band */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-8"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="hero-provider-stat-band"
            >
              {[
                { label: "MASCOT",       glow: "from-cyan-400/30 to-cyan-600/10",       color: "text-cyan-300",    border: "border-cyan-400/40" },
                { label: "BOOMING",      glow: "from-rose-500/30 to-rose-700/10",       color: "text-rose-300",    border: "border-rose-500/40" },
                { label: "BETSOFT",      glow: "from-amber-400/30 to-amber-600/10",     color: "text-amber-300",   border: "border-amber-400/40" },
                { label: "REDWOOD", glow: "from-violet-500/30 to-violet-700/10",   color: "text-violet-300",  border: "border-violet-500/40" },
                { label: "KA GAMING",    glow: "from-emerald-400/30 to-emerald-600/10", color: "text-emerald-300", border: "border-emerald-400/40" },
              ].filter(p => !isProviderHidden(p.label)).map((p, i) => (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.06 }}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border ${p.border} bg-gradient-to-br ${p.glow} backdrop-blur-sm shadow-[0_0_18px_rgba(255,255,255,0.06)]`}
                >
                  <span className={`text-[10px] sm:text-xs font-black tracking-[0.18em] ${p.color}`} style={{ fontFamily: "'Unbounded', sans-serif" }}>
                    {p.label}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* Grand Opening text banner removed per product 2026-06-23 */}

            {/* CTA Button */}
            <div className="flex flex-col items-center justify-center gap-4 mb-8">
              {isLive ? (
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => navigate("/signup")}
                    className="group flex items-center gap-2 bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-slate-950 font-black rounded-full px-10 py-4 text-lg transition-all hover:-translate-y-1 shadow-[0_0_40px_rgba(52,211,153,0.5)]"
                    data-testid="hero-play-now-btn"
                  >
                    PLAY NOW
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => setShowLogin(true)}
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white font-bold rounded-full px-10 py-4 text-lg transition-all"
                    data-testid="hero-login-btn"
                  >
                    <LogIn className="w-5 h-5" />
                    Log In
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate("/signup")}
                  className="group flex items-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-black rounded-full px-10 py-4 text-lg transition-all hover:-translate-y-1 shadow-[0_0_40px_rgba(251,191,36,0.5)]"
                  data-testid="hero-signup-btn"
                >
                  SIGN UP NOW · GET 300K GC{signupPromo?.grant_sc ? ` + ${signupPromo.grant_sc} SC` : ""} FREE
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
              <p className="text-slate-500 text-xs flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" /> FREE TO PLAY &bull; 18+ &bull; NO PURCHASE NECESSARY
              </p>
            </div>

            {/* Live player counter — Games/Bonus tiles hidden (Aug 2026) per
                Landing page cleanup ask; kept in state for lobby telemetry. */}
            {false && (
            <div className="relative mt-2">
              <div className="absolute inset-0 blur-2xl bg-gradient-to-r from-amber-500/10 via-emerald-500/10 to-amber-500/10 rounded-3xl" />
              <div className="relative flex items-center justify-center gap-3 sm:gap-6 text-center py-4 px-3 sm:px-6 rounded-2xl border border-amber-500/25 bg-black/40 backdrop-blur-sm shadow-[0_0_40px_-10px_rgba(251,191,36,0.35)]">
                {/* Active-Players tile hidden from public header per lobby-hardening ask (Feb 2026). Kept in state for internal metrics. */}
                {false && (
                <div className="flex flex-col items-center min-w-[80px]">
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                    </span>
                    <p className="text-2xl sm:text-4xl font-black bg-gradient-to-b from-white to-slate-300 bg-clip-text text-transparent tracking-tight" data-testid="hero-stat-players">
                      {Math.max(4287, livePlayersCount).toLocaleString()}+
                    </p>
                  </div>
                  <p className="text-[10px] sm:text-xs text-emerald-300/80 uppercase tracking-[0.15em] mt-1 font-semibold">Active Players</p>
                </div>
                )}
                {false && <div className="w-px h-12 bg-gradient-to-b from-transparent via-amber-500/40 to-transparent" />}
                <div className="flex flex-col items-center min-w-[80px]">
                  <p className="text-2xl sm:text-4xl font-black bg-gradient-to-b from-cyan-200 to-cyan-500 bg-clip-text text-transparent tracking-tight" data-testid="hero-stat-games">
                    {(publicStats?.games_count ?? 1586).toLocaleString()}+
                  </p>
                  <p className="text-[10px] sm:text-xs text-cyan-300/80 uppercase tracking-[0.15em] mt-1 font-semibold">Games</p>
                </div>
                <div className="w-px h-12 bg-gradient-to-b from-transparent via-amber-500/40 to-transparent" />
                <div className="flex flex-col items-center min-w-[80px]">
                  <p className="text-2xl sm:text-4xl font-black bg-gradient-to-b from-amber-300 to-amber-500 bg-clip-text text-transparent tracking-tight drop-shadow-[0_0_15px_rgba(251,191,36,0.4)]" data-testid="hero-stat-bonus">
                    300K
                  </p>
                  <p className="text-[10px] sm:text-xs text-amber-300/80 uppercase tracking-[0.15em] mt-1 font-semibold">Free GC Bonus</p>
                </div>
              </div>
            </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
                  PER-PROVIDER GAME COUNT — hero-adjacent
                  Shows the exact catalog size broken down by studio so
                  players see the depth (988 KA Gaming shooters + 226
                  Betsoft slots etc.). Auto-updates whenever /public-stats
                  refreshes.
              ═══════════════════════════════════════════════════════════ */}
            {publicStats?.provider_counts && (() => {
              const pc = publicStats.provider_counts;
              const tiles = [
                { key: "ka_gaming",  label: "KA Gaming",   count: pc.ka_gaming,  tint: "from-emerald-500/25 via-emerald-500/10 to-transparent", ring: "border-emerald-500/40",  text: "text-emerald-300" },
                { key: "betsoft",    label: "Betsoft",     count: pc.betsoft,    tint: "from-amber-500/25   via-amber-500/10   to-transparent", ring: "border-amber-500/40",    text: "text-amber-300"   },
                { key: "mascot",     label: "Mascot",      count: pc.mascot,     tint: "from-rose-500/25    via-rose-500/10    to-transparent", ring: "border-rose-500/40",     text: "text-rose-300"    },
                { key: "booming",    label: "Booming",     count: pc.booming,    tint: "from-cyan-500/25    via-cyan-500/10    to-transparent", ring: "border-cyan-500/40",     text: "text-cyan-300"    },
                { key: "peter_sons", label: "Redwood",count: pc.peter_sons, tint: "from-violet-500/25  via-violet-500/10  to-transparent", ring: "border-violet-500/40",   text: "text-violet-300"  },
                { key: "spinjoy",    label: "Spinjoy",     count: pc.spinjoy,    tint: "from-pink-500/25    via-pink-500/10    to-transparent", ring: "border-pink-500/40",     text: "text-pink-300"    },
                { key: "jackroyals", label: "JackRoyals",  count: pc.jackroyals, tint: "from-fuchsia-500/25 via-fuchsia-500/10 to-transparent", ring: "border-fuchsia-500/40",  text: "text-fuchsia-300" },
              ].filter(t => (t.count ?? 0) > 0);
              return (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-7 gap-2 sm:gap-3 max-w-5xl mx-auto" data-testid="provider-breakdown">
                  {tiles.map(t => (
                    <div key={t.key}
                         className={`relative rounded-xl border ${t.ring} bg-gradient-to-b ${t.tint} px-2 py-3 text-center transition-transform hover:-translate-y-0.5`}>
                      <p className={`text-lg sm:text-2xl font-black tabular-nums ${t.text}`}>{Number(t.count).toLocaleString()}</p>
                      <p className="text-[9px] sm:text-[10px] tracking-wider text-slate-400 font-semibold uppercase mt-0.5">{t.label}</p>
                    </div>
                  ))}
                </div>
              );
            })()}
          </motion.div>
        </div>
      </section>

      {/* Live Winner Ticker — removed until site goes live (pre-launch) */}

      {/* ═══ MEGA LOBBY — Slots · Fish · Shooting rows with gold velvet ropes (Aug 2026) ═══ */}
      <MegaLobby
        slotsTiles={allGames.map((g) => ({ id: g.name, name: g.name, thumb: g.image, provider: g.provider || "JackRoyals" }))}
        onTileClick={(tile) => navigate(user ? `/lobby?launch=${encodeURIComponent(tile.id)}` : "/signup")}
      />

      {/* ═══ PROVIDERS BAND — animated provider showcase ═══ */}
      <section className="relative py-10 md:py-14 overflow-hidden border-y border-amber-500/15" data-testid="providers-band">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/[0.04] to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-center mb-6">
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-cyan-400">Powered By Top Studios</span>
            <h3 className="text-2xl md:text-3xl font-black tracking-tight text-white mt-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Industry-Leading Game Providers
            </h3>
          </div>
          <div className="overflow-hidden">
            <div className="jr-marquee flex gap-12 md:gap-16 items-center whitespace-nowrap">
              {[
                { name: "MASCOT", tag: "GAMING", c: "from-rose-500 to-amber-400" },
                { name: "BOOMING", tag: "GAMES", c: "from-cyan-400 to-blue-500" },
                { name: "REDWOOD", tag: "STUDIO", c: "from-emerald-400 to-teal-500" },
                { name: "BETSOFT", tag: "GAMING", c: "from-amber-400 to-orange-500" },
                { name: "KA GAMING", tag: "SHOOTERS", c: "from-emerald-400 to-lime-500" },
                { name: "JACKROYALS", tag: "ORIGINALS", c: "from-purple-400 to-pink-500" },
                { name: "MASCOT", tag: "GAMING", c: "from-rose-500 to-amber-400" },
                { name: "BOOMING", tag: "GAMES", c: "from-cyan-400 to-blue-500" },
                { name: "REDWOOD", tag: "STUDIO", c: "from-emerald-400 to-teal-500" },
                { name: "BETSOFT", tag: "GAMING", c: "from-amber-400 to-orange-500" },
                { name: "KA GAMING", tag: "SHOOTERS", c: "from-emerald-400 to-lime-500" },
                { name: "JACKROYALS", tag: "ORIGINALS", c: "from-purple-400 to-pink-500" },
              ].filter(p => !isProviderHidden(p.name)).map((p, i) => (
                <div key={i} className="flex-shrink-0 group cursor-default">
                  <div className="px-6 py-3 rounded-xl bg-gradient-to-r from-white/[0.03] to-white/[0.06] border border-white/10 hover:border-amber-400/40 transition-all">
                    <p className={`text-lg md:text-2xl font-black tracking-tighter bg-gradient-to-r ${p.c} bg-clip-text text-transparent`} style={{ fontFamily: "'Unbounded', sans-serif" }}>
                      {p.name}
                    </p>
                    <p className="text-[10px] tracking-[0.3em] text-slate-500 mt-0.5 font-bold">{p.tag}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { n: "1,300+", l: "Games", c: "text-amber-400" },
              { n: "5", l: "Providers", c: "text-cyan-400" },
              { n: "24/7", l: "Play Anytime", c: "text-emerald-400" },
              { n: "INSTANT", l: "Prize Redemption", c: "text-rose-400" },
            ].map((s, i) => (
              <div key={i} className="text-center p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-amber-400/30 transition-all">
                <div className={`text-2xl md:text-3xl font-black ${s.c}`} style={{ fontFamily: "'Unbounded', sans-serif" }}>{s.n}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1 font-bold">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ POPULAR RIGHT NOW · LIVE COUNTER + HOT BADGES ═══ */}
      {/* Independent of the featured-games fetch — hardcoded 4-game FOMO row
          that renders even if the /featured-games API is slow or empty. */}
      <div id="popular-now" data-testid="popular-now-anchor" />
      <PopularNowWidget onPlay={() => navigate("/signup")} />

      {/* ═══ FEATURED GAMES GRID ═══ */}
      {allGames.length > 0 && (
        <section id="games" className="relative py-16 md:py-24 overflow-hidden" data-testid="landing-games-section">
          {/* Neon background flourishes */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full bg-cyan-500/[0.06] blur-[120px]" />
            <div className="absolute bottom-0 right-0 w-[460px] h-[460px] rounded-full bg-amber-400/[0.07] blur-[140px]" />
            <div className="absolute top-1/2 left-1/2 w-[280px] h-[280px] rounded-full bg-rose-500/[0.05] blur-[100px]" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="text-[10px] sm:text-xs font-black tracking-[0.4em] uppercase bg-gradient-to-r from-cyan-300 via-amber-300 to-rose-300 bg-clip-text text-transparent">
                ◆ ALL PROVIDERS · ONE LOBBY ◆
              </span>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mt-3" style={{ fontFamily: "'Unbounded', sans-serif", textShadow: "0 0 30px rgba(251,191,36,0.25)" }}>
                Top Rated <span className="bg-gradient-to-r from-amber-300 via-rose-300 to-cyan-300 bg-clip-text text-transparent">Games</span>
              </h2>
              <p className="text-slate-400 mt-3 max-w-xl mx-auto text-sm sm:text-base">
                Hand-picked hits from {[!isProviderHidden("Mascot") && "Mascot", !isProviderHidden("Booming") && "Booming", !isProviderHidden("Betsoft") && "Betsoft", !isProviderHidden("Redwood") && "Redwood"].filter(Boolean).join(", ").replace(/, ([^,]*)$/, " and $1")} — refreshed every week. Sign up to spin them all for free.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
              {allGames.slice(0, 18).map((game, i) => {
                const chip = providerChip(game.provider);
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ delay: (i % 12) * 0.04, type: "spring", stiffness: 110, damping: 18 }}
                    whileHover={{ y: -6 }}
                    className="group relative bg-gradient-to-br from-slate-900/95 to-slate-950 border border-white/[0.06] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-amber-400/40 hover:shadow-[0_0_35px_rgba(251,191,36,0.18)]"
                    onClick={() => navigate("/signup")}
                    data-testid={`landing-game-${i}`}
                  >
                    {/* Tracing-beam glow on hover */}
                    <span className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          style={{ background: "conic-gradient(from 180deg at 50% 50%, rgba(251,191,36,0.18), rgba(244,63,94,0.18), rgba(34,211,238,0.18), rgba(251,191,36,0.18))",
                                   filter: "blur(14px)", zIndex: 0 }} />
                    {/* Provider chip */}
                    <div className="absolute top-2 left-2 z-20">
                      <span className={`px-1.5 py-0.5 rounded-md border text-[8px] sm:text-[9px] font-black tracking-[0.12em] backdrop-blur-sm ${chip.cls}`} style={{ fontFamily: "'Unbounded', sans-serif" }}>
                        {chip.text}
                      </span>
                    </div>
                    <div className="relative z-10 aspect-square overflow-hidden bg-black">
                      <img
                        src={game.image}
                        alt={game.name}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                    {/* Play-free overlay on hover */}
                    <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-12">
                      <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-black text-[10px] sm:text-xs px-4 py-1.5 rounded-full shadow-[0_0_20px_rgba(251,191,36,0.6)]">
                        <Play className="w-3 h-3" /> PLAY FREE
                      </div>
                    </div>
                    <div className="relative z-10 px-3 py-2.5 border-t border-white/[0.04]">
                      <p className="text-[11px] sm:text-xs font-bold text-white truncate" style={{ fontFamily: "'Unbounded', sans-serif" }}>{game.name}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="text-center mt-10">
              <button
                onClick={() => navigate("/signup")}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 text-amber-300 hover:text-amber-200 font-black text-sm tracking-wider transition-all shadow-[0_0_25px_rgba(251,191,36,0.25)]"
                data-testid="landing-view-all-games-btn"
                style={{ fontFamily: "'Unbounded', sans-serif" }}
              >
                EXPLORE ALL {Math.max(1050, publicStats?.games_count || 0).toLocaleString()}+ GAMES
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-16 md:py-24 bg-slate-900/30" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-amber-400">Get Started In Minutes</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mt-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              How Jack Royals Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { icon: UserPlus, color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20", title: "Sign Up Free", desc: "Create your account in seconds. Get 300,000 Gold Coins + 2 FREE Sweeps Coins instantly." },
              { icon: Gamepad2, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", title: "Play Arcade Games", desc: "Enjoy 1,300+ slots, fish games, shooters, and arcade classics. Play with GC for fun or SC for prizes." },
              { icon: Gift, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", title: "Redeem SC for Prizes", desc: "Won Sweeps Coins? Redeem them for real cash prizes. It's that simple." },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl border ${step.bg} mb-5`}>
                  <step.icon className={`w-7 h-7 ${step.color}`} />
                </div>
                <div className="text-xs font-bold text-slate-500 mb-2">STEP {i + 1}</div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Giveaway section with PS5/TV/iPad imagery removed 2026-06-23 per product */}

      {/* ═══ TRUST / WHY JACKROYALS ═══ */}
      <section className="py-16 md:py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-amber-400">Safe & Legal</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mt-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Why Jack Royals?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "100% Legal", desc: "U.S. sweepstakes compliant. No gambling license needed to play.", color: "text-sky-400" },
              { icon: Zap, title: "No Purchase Necessary", desc: "Free entry method available. Play and win without spending a dime.", color: "text-amber-400" },
              { icon: Crown, title: "VIP Rewards", desc: "6 VIP tiers with increasing benefits, daily bonuses, and exclusive perks.", color: "text-purple-400" },
              { icon: Check, title: "Fast Redemptions", desc: "Verified players can redeem Sweeps Coins for real cash prizes.", color: "text-emerald-400" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="backdrop-blur-xl bg-slate-900/60 border border-white/5 rounded-2xl p-6 text-center hover:-translate-y-1 transition-transform"
              >
                <item.icon className={`w-8 h-8 ${item.color} mx-auto mb-4`} />
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-amber-500/[0.05] rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Don't Miss Out
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Sign up now and get <span className="text-amber-400 font-bold">300,000 Gold Coins</span> + <span className="text-emerald-300 font-bold">2 FREE Sweeps Coins</span> on the house — explore 1,300+ games from 5 world-class providers from your very first spin.
          </p>
          <button
            onClick={() => navigate("/signup")}
            className="group flex items-center gap-2 mx-auto bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-black rounded-full px-10 py-4 text-lg transition-all hover:-translate-y-1 shadow-[0_0_30px_rgba(251,191,36,0.4)]"
            data-testid="final-cta-btn"
          >
            SIGN UP NOW · GET 300K GC + 2 SC FREE
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-xs text-slate-600 mt-4">No purchase necessary. Must be 18+. Free to enter.</p>
        </div>
      </section>

      {/* ═══ TRUST SIGNALS GRID ═══ */}
      <TrustSignalsGrid />

      {/* ═══ PAYMENT TRUST STRIP ═══ */}
      <PaymentTrustStrip />

      {/* ═══ INSTAGRAM CREW STRIP (was Facebook Community · pivoted Aug 2026) ═══ */}
      <section
        className="relative overflow-hidden border-y border-pink-500/25 py-10 px-4"
        style={{ background: "linear-gradient(90deg, #1a0b2e 0%, #46145e 45%, #1a0b2e 100%)" }}
        data-testid="landing-instagram-strip"
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #833AB4 0%, #E1306C 45%, #F77737 85%)",
                boxShadow: "0 8px 24px rgba(225,48,108,0.5)",
              }}
            >
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-black text-white tracking-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                Join the <span className="bg-gradient-to-r from-amber-300 via-pink-400 to-fuchsia-400 bg-clip-text text-transparent">@JackRoyals</span> Crew
              </h3>
              <p className="text-sm text-pink-100/80 mt-0.5">
                Daily SC drops, win highlights, and exclusive lifestyle rewards.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-instagram-cta"
              className="inline-flex items-center gap-2 px-6 sm:px-7 py-3 rounded-full font-black text-white text-sm uppercase tracking-wider transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
              style={{
                background: "linear-gradient(135deg, #833AB4 0%, #E1306C 45%, #F77737 85%)",
                boxShadow: "0 10px 32px rgba(225,48,108,0.5)",
              }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              Follow Now
            </a>
            {/* Egyptian Queen mascot — sits on the right on desktop, top-right corner on mobile */}
            <Mascot
              slug="egyptian_princess"
              size="md"
              role="inline"
              tip="Slay the reels today 👸"
              className="!w-20 !h-20 sm:!w-24 sm:!h-24"
            />
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="bg-[#020617] border-t border-white/5 pt-12 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={LOGO_URL} alt="JackRoyals" className="w-8 h-8 rounded-lg" />
                <span className="font-bold text-white">JackRoyals</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Jack Royals LLC, registered in Wyoming, USA. A sweepstakes entertainment platform.
              </p>
              {/* Instagram-only social CTA — Valkyrie signals daily battles + giveaways (Aug 2026) */}
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-cta"
                className="group mt-4 inline-flex items-center gap-3 pl-2 pr-4 py-2 rounded-full transition-all hover:scale-[1.03]"
                style={{ background: "linear-gradient(135deg, #833AB4, #E1306C 45%, #F77737 85%)" }}
              >
                <Mascot slug="viking_warrior" size="sm" role="inline" tip="Daily Battles & Giveaways ⚔️" className="!w-10 !h-10" />
                <div className="text-left">
                  <p className="text-[10px] font-bold text-white/90 leading-none tracking-wider uppercase">Follow</p>
                  <p className="text-sm font-black text-white leading-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                    @JackRoyals
                  </p>
                  <p className="text-[10px] text-white/90 leading-none">Daily SC Drops</p>
                </div>
              </a>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Legal</h4>
              <div className="space-y-2">
                <span onClick={() => navigate("/terms")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Terms of Service</span>
                <span onClick={() => navigate("/privacy")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Privacy Policy</span>
                <span onClick={() => navigate("/sweepstakes-rules")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Official Sweepstakes Rules</span>
                <span onClick={() => navigate("/amoe")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Free Entry (AMOE)</span>
                <span onClick={() => navigate("/compliance")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Compliance Summary</span>
                <span onClick={() => navigate("/refund-policy")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Refund Policy</span>
                <span onClick={() => navigate("/shipping-policy")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Shipping & Delivery</span>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Responsible Play</h4>
              <div className="space-y-2">
                <span onClick={() => navigate("/responsible-gaming")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Responsible Gaming</span>
                <span onClick={() => navigate("/responsible-gaming")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Self-Exclusion</span>
                <span onClick={() => navigate("/responsible-gaming")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Play Limits & Tools</span>
                <a href="https://www.ncpgambling.org" target="_blank" rel="noopener noreferrer" className="block text-xs text-slate-500 hover:text-white transition-colors">NCPG Resources</a>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Support</h4>
              <div className="space-y-2">
                <span onClick={() => navigate("/faq")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">FAQ</span>
                <span onClick={() => navigate("/contact")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Contact Us</span>
                <span onClick={() => navigate("/security")} className="block text-xs text-slate-500 hover:text-white transition-colors cursor-pointer">Security Policy</span>
                <a href="mailto:support@jackroyals.com" className="block text-xs text-slate-500 hover:text-amber-400 transition-colors" data-testid="footer-support-email">support@jackroyals.com</a>
                <p className="text-xs text-slate-500">Helpline: 1-800-522-4700</p>
              </div>
            </div>
          </div>

          {/* Business / Partner Contacts */}
          <div className="border-t border-white/5 pt-6 mb-6">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">Business &amp; Partner Inquiries</h4>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px]">
              <a href="mailto:partnerships@jackroyals.com" className="text-slate-400 hover:text-fuchsia-400 transition-colors" data-testid="footer-partnerships-email">
                <strong className="text-slate-300">Partnerships:</strong> partnerships@jackroyals.com
              </a>
              <a href="mailto:integration@jackroyals.com" className="text-slate-400 hover:text-orange-400 transition-colors" data-testid="footer-integration-email">
                <strong className="text-slate-300">Integration:</strong> integration@jackroyals.com
              </a>
              <a href="mailto:compliances@jackroyals.com" className="text-slate-400 hover:text-emerald-400 transition-colors" data-testid="footer-compliance-email">
                <strong className="text-slate-300">Compliance:</strong> compliances@jackroyals.com
              </a>
              <a href="mailto:billing@jackroyals.com" className="text-slate-400 hover:text-rose-400 transition-colors" data-testid="footer-billing-email">
                <strong className="text-slate-300">Billing:</strong> billing@jackroyals.com
              </a>
            </div>
          </div>

          <div className="border-t border-white/5 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium">U.S. Sweepstakes Compliant</span>
              <span className="text-[10px] px-2 py-1 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20 font-medium">No Purchase Necessary</span>
              <span className="text-[10px] px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 font-medium">18+ Only</span>
            </div>
            <p className="text-[10px] text-slate-600 text-center md:text-right">
              &copy; 2026 Jack Royals LLC. All rights reserved. Gold Coins are virtual currency for entertainment. Sweeps Coins are free promotional entries.
            </p>
          </div>
        </div>
      </footer>

      {/* ═══ STICKY MOBILE CTA ═══ */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-[#020617]/90 backdrop-blur-xl border-t border-white/10 p-3 safe-area-inset-bottom" data-testid="sticky-mobile-cta">
        <button
          onClick={() => navigate("/signup")}
          className="w-full bg-amber-400 hover:bg-amber-500 text-slate-950 font-black rounded-full py-3.5 text-base shadow-[0_0_20px_rgba(251,191,36,0.4)] flex items-center justify-center gap-2"
        >
          SIGN UP · 300K GC FREE <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* ═══ LOGIN MODAL ═══ */}
      <Dialog open={showLogin} onOpenChange={closeLoginModal}>
        <DialogContent className="bg-transparent border-none shadow-none p-0 max-w-md overflow-visible">
          <div className="relative">
            {/* Glow backdrop */}
            <div className="absolute -inset-4 rounded-3xl blur-2xl opacity-40" style={{ background: 'radial-gradient(circle, rgba(255,180,50,0.4) 0%, rgba(255,100,0,0.15) 50%, transparent 70%)' }} />
            
            {/* Card */}
            <motion.div 
              className="relative rounded-2xl overflow-hidden border border-amber-500/20"
              style={{ background: 'linear-gradient(165deg, #1a1230 0%, #0d0a1a 40%, #0a0812 100%)' }}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              {/* Decorative top strip */}
              <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #ff6b00, #ffb400, #ff6b00)' }} />
              
              {/* Header with mascot */}
              <div className="relative px-6 pt-6 pb-4 text-center overflow-hidden">
                {/* Floating particles */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(5)].map((_, i) => (
                    <motion.div 
                      key={i}
                      className="absolute w-1 h-1 rounded-full bg-amber-400/40"
                      style={{ left: `${15 + i * 18}%`, top: `${20 + (i % 3) * 25}%` }}
                      animate={{ y: [-5, 5, -5], opacity: [0.3, 0.7, 0.3] }}
                      transition={{ duration: 2 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
                    />
                  ))}
                </div>
                
                {/* Mascot */}
                <motion.div 
                  className="relative mx-auto w-20 h-20 mb-3"
                  animate={{ y: [-3, 3, -3] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="absolute -inset-2 rounded-full bg-amber-500/15 blur-lg" />
                  <img 
                    src="/bot-mascot.jpg" 
                    alt="JackRoyals" 
                    className="w-20 h-20 rounded-full object-cover ring-2 ring-amber-500/50 shadow-[0_0_20px_rgba(255,180,50,0.3)]" 
                  />
                </motion.div>
                
                <DialogHeader className="space-y-1">
                  <DialogTitle className="text-2xl font-black text-white tracking-tight" style={{ textShadow: '0 0 20px rgba(255,180,50,0.3)' }}>
                    {resetMode ? (resetDone ? "Password Reset!" : "Set New Password") : forgotMode ? (forgotSent ? "Check Your Email" : "Reset Password") : "Welcome Back"}
                  </DialogTitle>
                  <p className="text-amber-200/50 text-xs font-medium tracking-wide uppercase">
                    {resetMode ? (resetDone ? "You can now log in" : "Enter your new password") : forgotMode ? (forgotSent ? "Reset link sent" : "Enter your email") : "Log in to play & win"}
                  </p>
                </DialogHeader>
              </div>

              {/* Reset Password Form */}
              {resetMode ? (
                <div className="px-6 pb-6 space-y-3">
                  {resetDone ? (
                    <>
                      <div className="text-center py-4">
                        <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                          <Check className="w-7 h-7 text-green-400" />
                        </div>
                        <p className="text-white/70 text-sm">Your password has been reset successfully.</p>
                      </div>
                      <motion.button
                        type="button"
                        onClick={() => { setResetMode(false); setResetDone(false); setResetToken(""); }}
                        className="w-full font-extrabold rounded-xl py-3.5 text-sm uppercase tracking-widest"
                        style={{ background: 'linear-gradient(135deg, #ff8c00 0%, #ffb400 50%, #ff8c00 100%)', color: '#1a0a00', boxShadow: '0 4px 20px rgba(255,140,0,0.35)' }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        data-testid="back-to-login-btn"
                      >
                        Back to Login
                      </motion.button>
                    </>
                  ) : (
                    <form onSubmit={handleResetPassword} className="space-y-3">
                      <div className="relative group">
                        <input type="password" placeholder="New Password" value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} className="relative w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-sm focus:border-amber-400/40 focus:outline-none focus:bg-white/[0.06] transition-all placeholder:text-white/20" required data-testid="reset-password-input" />
                      </div>
                      <div className="relative group">
                        <input type="password" placeholder="Confirm Password" value={resetForm.confirm} onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })} className="relative w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-sm focus:border-amber-400/40 focus:outline-none focus:bg-white/[0.06] transition-all placeholder:text-white/20" required data-testid="reset-confirm-input" />
                      </div>
                      <motion.button type="submit" disabled={resetLoading} className="relative w-full font-extrabold rounded-xl py-3.5 text-sm uppercase tracking-widest transition-all disabled:opacity-50 overflow-hidden" style={{ background: 'linear-gradient(135deg, #ff8c00 0%, #ffb400 50%, #ff8c00 100%)', color: '#1a0a00', boxShadow: '0 4px 20px rgba(255,140,0,0.35)' }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} data-testid="reset-submit-btn">
                        <span>{resetLoading ? "Resetting..." : "Reset Password"}</span>
                      </motion.button>
                    </form>
                  )}
                </div>
              ) : forgotMode ? (
                <div className="px-6 pb-6 space-y-3">
                  {forgotSent ? (
                    <>
                      <div className="text-center py-4">
                        <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
                          <Shield className="w-7 h-7 text-amber-400" />
                        </div>
                        <p className="text-white/70 text-sm">If an account exists with that email, you'll receive a password reset link. Check your inbox.</p>
                      </div>
                      <motion.button
                        type="button"
                        onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(""); }}
                        className="w-full font-extrabold rounded-xl py-3.5 text-sm uppercase tracking-widest"
                        style={{ background: 'linear-gradient(135deg, #ff8c00 0%, #ffb400 50%, #ff8c00 100%)', color: '#1a0a00', boxShadow: '0 4px 20px rgba(255,140,0,0.35)' }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        data-testid="back-to-login-btn"
                      >
                        Back to Login
                      </motion.button>
                    </>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-3">
                      <div className="relative group">
                        <input type="text" placeholder="Email or Username" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="relative w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-sm focus:border-amber-400/40 focus:outline-none focus:bg-white/[0.06] transition-all placeholder:text-white/20" required data-testid="forgot-email-input" />
                      </div>
                      <motion.button type="submit" disabled={forgotLoading} className="relative w-full font-extrabold rounded-xl py-3.5 text-sm uppercase tracking-widest transition-all disabled:opacity-50 overflow-hidden" style={{ background: 'linear-gradient(135deg, #ff8c00 0%, #ffb400 50%, #ff8c00 100%)', color: '#1a0a00', boxShadow: '0 4px 20px rgba(255,140,0,0.35)' }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} data-testid="forgot-submit-btn">
                        <span>{forgotLoading ? "Sending..." : "Send Reset Link"}</span>
                      </motion.button>
                      <button type="button" onClick={() => setForgotMode(false)} className="w-full text-xs text-amber-400/60 hover:text-amber-400 transition-colors py-2" data-testid="back-to-login-link">Back to Login</button>
                    </form>
                  )}
                </div>
              ) : (
              /* Login Form */
              <form onSubmit={handleLogin} className="px-6 pb-6 space-y-3">
                <div className="relative group">
                  <div className="absolute inset-0 rounded-xl bg-amber-500/5 opacity-0 group-focus-within:opacity-100 transition-opacity -m-0.5" />
                  <input
                    type="text"
                    placeholder="Email or Username"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="relative w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-sm focus:border-amber-400/40 focus:outline-none focus:bg-white/[0.06] transition-all placeholder:text-white/20"
                    required
                    data-testid="login-email"
                  />
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 rounded-xl bg-amber-500/5 opacity-0 group-focus-within:opacity-100 transition-opacity -m-0.5" />
                  <input
                    type="password"
                    placeholder="Password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="relative w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-sm focus:border-amber-400/40 focus:outline-none focus:bg-white/[0.06] transition-all placeholder:text-white/20"
                    required
                    data-testid="login-password"
                  />
                </div>

                <div className="flex justify-end">
                  <button type="button" onClick={() => setForgotMode(true)} className="text-xs text-amber-400/60 hover:text-amber-400 transition-colors" data-testid="forgot-password-btn">Forgot password?</button>
                </div>

                {/* Actionable login error with one-click CTA — saves the 23% of users who churn at "Invalid credentials" */}
                {loginError && (
                  <div
                    className="rounded-xl border border-rose-500/30 bg-rose-500/[0.08] px-4 py-3 text-xs text-rose-100/90 leading-relaxed"
                    data-testid="login-error-banner"
                  >
                    <p className="mb-2">{loginError.message}</p>
                    {loginError.action === "signup" && (
                      <button
                        type="button"
                        onClick={() => { setShowLogin(false); navigate("/signup"); }}
                        className="inline-flex items-center gap-1.5 font-bold text-amber-300 hover:text-amber-200 underline underline-offset-2"
                        data-testid="login-error-signup-cta"
                      >
                        Create an account →
                      </button>
                    )}
                    {loginError.action === "forgot_password" && (
                      <button
                        type="button"
                        onClick={() => {
                          setForgotEmail(loginError.identifier || loginForm.email || "");
                          setForgotMode(true);
                          setLoginError(null);
                        }}
                        className="inline-flex items-center gap-1.5 font-bold text-amber-300 hover:text-amber-200 underline underline-offset-2"
                        data-testid="login-error-forgot-cta"
                      >
                        Reset your password →
                      </button>
                    )}
                  </div>
                )}

                <motion.button
                  type="submit"
                  disabled={loginLoading}
                  className="relative w-full font-extrabold rounded-xl py-3.5 text-sm uppercase tracking-widest transition-all disabled:opacity-50 overflow-hidden"
                  style={{ 
                    background: 'linear-gradient(135deg, #ff8c00 0%, #ffb400 50%, #ff8c00 100%)',
                    color: '#1a0a00',
                    boxShadow: '0 4px 20px rgba(255,140,0,0.35), inset 0 1px 0 rgba(255,255,255,0.2)'
                  }}
                  whileHover={{ scale: 1.02, boxShadow: '0 6px 30px rgba(255,140,0,0.5)' }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="login-submit"
                >
                  {/* Shimmer effect */}
                  <motion.div 
                    className="absolute inset-0 opacity-30"
                    style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)' }}
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }}
                  />
                  <span className="relative">{loginLoading ? "Logging in..." : "Log In"}</span>
                </motion.button>

                {/* Welcome bonus teaser */}
                <div className="flex items-center gap-2 justify-center pt-2">
                  <div className="flex -space-x-1">
                    <div className="w-4 h-4 rounded-full bg-amber-500/80 flex items-center justify-center ring-1 ring-amber-600/50">
                      <Crown className="w-2.5 h-2.5 text-amber-900" />
                    </div>
                    <div className="w-4 h-4 rounded-full bg-emerald-500/80 flex items-center justify-center ring-1 ring-emerald-600/50">
                      <Sparkles className="w-2.5 h-2.5 text-emerald-900" />
                    </div>
                  </div>
                  <p className="text-[10px] text-white/30 font-medium">Daily bonuses & rewards waiting for you</p>
                </div>
              </form>
              )}
            </motion.div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Unbounded:wght@400;700;900&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
    </div>
  );
};

export default LandingPage;
